import { useMutation } from '@apollo/client';
import { useLanguage } from '@vocab/react';
import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { COUNTRY } from 'src/app/constants/configObjectProperties';
import { LANGUAGE_DEFAULT } from 'src/app/constants/language';
import { actionUpdateQuestionnaireUrl } from 'src/app/state/modules/formData/questionnaireUrl';
import { getConfig } from 'src/app/utils/config';
import { log } from 'src/app/utils/logging';
import { CREATE_QUESTIONNAIRE } from 'src/graphql/roleReqs';
import type AppState from 'src/types/appState';

export const useQuestionnaire = () => {
  const { advertiserId, seekAdvertiserId } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session?.advertiserId,
      seekAdvertiserId: state.session?.seekAdvertiserId,
    }),
    shallowEqual,
  );
  const language = useLanguage().language ?? LANGUAGE_DEFAULT;
  const dispatch = useDispatch();
  // Temporary default, till we investigate it further issue related to create questionnaire
  const countryCode: string = getConfig(COUNTRY) ?? 'AU';
  const [createQuestionnaire] = useMutation(CREATE_QUESTIONNAIRE, {
    onCompleted(data) {
      if (data?.createQuestionnaire) {
        dispatch(
          actionUpdateQuestionnaireUrl(
            data?.createQuestionnaire?.questionnaireUrl,
          ),
        );
      }
    },
    onError: (error) => {
      log.error('useQuestionnaire | Creating questionnaire via graph failed', {
        error,
        advertiserId,
        seekAdvertiserId,
        countryCode,
      });
    },
  });

  const setQuestionnaire = useCallback(
    (sourceQuestionnaireId?: string) =>
      createQuestionnaire({
        variables: {
          input: {
            advertiserId: seekAdvertiserId,
            domainCountryCode: countryCode.toLowerCase(),
            language,
            sourceQuestionnaireId,
            adCentreAdvertiserId: advertiserId,
          },
        },
      }),
    [
      advertiserId,
      countryCode,
      createQuestionnaire,
      seekAdvertiserId,
      language,
    ],
  );

  return setQuestionnaire;
};
